import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';

interface Document {
  id: string;
  title: string;
  status: string;
  productionId?: string;
  createdAt: string;
}

export default function Documents() {
  const [searchParams] = useSearchParams();
  const productionId = searchParams.get('productionId');
  const status = searchParams.get('status');

  const { data, isLoading } = useQuery({
    queryKey: ['documents', productionId, status],
    queryFn: () =>
      api.get<{ documents: Document[] }>('/documents', {
        ...(productionId && { productionId }),
        ...(status && { status }),
      }),
  });

  const documents = data?.data.documents || [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Documents</h1>
        <Link to="/documents/new" className="btn btn-primary">
          New Document
        </Link>
      </div>

      <div className="filters">
        <div className="filter-tabs">
          <Link
            to="/documents"
            className={`tab ${!searchParams.get('status') ? 'active' : ''}`}
          >
            All
          </Link>
          <Link to="/documents?status=draft" className="tab">
            Drafts
          </Link>
          <Link to="/documents?status=pending_signatures" className="tab">
            Pending
          </Link>
          <Link to="/documents?status=completed" className="tab">
            Completed
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="empty-state">
          <p>No documents found</p>
          <Link to="/documents/new" className="btn btn-primary">
            Create Your First Document
          </Link>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <Link to={`/documents/${doc.id}`} className="link">
                      {doc.title || 'Untitled Document'}
                    </Link>
                  </td>
                  <td>
                    <span className={`badge badge-${doc.status}`}>
                      {doc.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{new Date(doc.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <Link
                        to={`/documents/${doc.id}`}
                        className="btn btn-small"
                      >
                        View
                      </Link>
                      {doc.status === 'completed' && (
                        <a
                          href={`/api/documents/${doc.id}/download`}
                          className="btn btn-small btn-secondary"
                          download
                        >
                          Download
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
